// Shared clicker types and inline icons used by the clicker view, the per-device
// launch panel, and the clicker info/log modals. Split out so those views don't
// have to import each other just to agree on the device-config shape and icons.
import { can } from '../core/access';
import { appState } from '../core/state';
import { t } from '../ui/lang';
import { esc } from '../ui/modal';
/** The "Account number" field of an AdsPower profile's settings (read back from `.dc-account`). */
export function accountNumberFieldHtml(value) {
    return `<div class="modal__field">
                <label>${t('clicker.account_number')}</label>
                <input class="settings-input dc-account" type="text" autocomplete="off" placeholder="${t('clicker.account_number_ph')}" value="${esc(value ?? '')}" />
                <p class="modal__hint">${t('clicker.account_number_hint')}</p>
            </div>`;
}
let clickerRunning = { counts: {}, udids: new Set() };
export function getClickerRunning() {
    return clickerRunning;
}
export async function refreshClickerRunning() {
    if (!can('clicker.running'))
        return clickerRunning;
    try {
        const res = await fetch('/api/clicker/running');
        if (res.ok) {
            const data = await res.json();
            clickerRunning = { counts: data.counts ?? {}, udids: new Set(data.udids ?? []) };
        }
    }
    catch { /* keep last-known */ }
    return clickerRunning;
}
/**
 * Confirm before stopping a clicker. A stop is a SIGTERM to a run that may be
 * mid-payout: the clicker closes the task it is holding as cancelled, and the
 * transfer may already have gone through — so it is not a free click. Every Stop
 * control routes through this (stream card, launch panel, duo and geelark grids)
 * so the wording is identical everywhere. `label` is the device name when the
 * surface knows one.
 */
/**
 * UX mirror of the server's stop rule (farmClicker.ts `mayStopClicker`): may the current
 * user stop a clicker started by `startedBy`? Senior operators and above may stop anyone's
 * run; below that, only your own start. An unknown starter ('' — an automatic resume, a
 * start from the phone's own card, or one aged out of the audit log) is stoppable by
 * anyone, exactly as the server treats it. The server is the real guard; this just keeps
 * the UI from offering a button that would 403.
 */
export function canStopClicker(startedBy) {
    if (can('clicker.stopAny'))
        return true;
    return !startedBy || startedBy === appState.currentUserLogin;
}
/** Why the Stop button is disabled — same sentence the server's 403 carries. */
export function stopBlockedReason(startedBy) {
    return t('clicker.stop_blocked', { user: String(startedBy) });
}
export function confirmClickerStop(label) {
    return confirm(label
        ? t('clicker.confirm_stop_on_device', { device: label })
        : t('clicker.confirm_stop'));
}
export const PLAY_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>`;
export const STOP_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="1.5"/></svg>`;
export const LOG_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 5h16M4 10h16M4 15h10M4 20h7"/></svg>`;
// Ledger/table glyph for the operations registry — a framed table with a header row and
// a first column, so it reads as "registry" and stays distinct from the log lines above.
export const REGISTRY_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M9 9v11"/></svg>`;
// Clock + rewind arc for the clicker run history (start/stop audit). Same glyph as the
// stream cards' clickerHistorySvg, sized/stroked like the icons above.
export const HISTORY_ICON = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 12a8.5 8.5 0 1 0 2.9-6.4L3 8"/><path d="M3 3.5V8h4.5"/><path d="M12 7.5V12l3 1.8"/></svg>`;
